export type BoardDirectorHistoryEntry = {
    quarterEndDate: string
    submissionDate: string
    title: string
    position: string
    currentStatus: string
    disqualified: string
}

export type BoardDirector = {
    title: string
    name: string
    din: string
    pan: string
    position: string
    currentStatus: string
    disqualified: string
    history: BoardDirectorHistoryEntry[]
}

export type BoardOfDirectorsDocument = {
    symbol: string
    exchange: string
    asOfQuarterEndDate: string
    updatedAt: string
    directorCount: number
    directors: BoardDirector[]
}

export type DirectorRoleHistoryEntry = {
    symbol: string
    exchange: string
    quarterEndDate: string
    submissionDate: string
    title: string
    name: string
    position: string
    currentStatus: string
    disqualified: string
}

export type DirectorByDinDocument = {
    din: string
    name: string
    title: string
    pan: string
    updatedAt: string
    roleCount: number
    roles: DirectorRoleHistoryEntry[]
}

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined
    }
    return value as Record<string, unknown>
}

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

const parseHistoryEntry = (raw: unknown): BoardDirectorHistoryEntry | undefined => {
    const record = asRecord(raw)
    if (!record) {
        return undefined
    }
    return {
        quarterEndDate: asString(record.quarterEndDate),
        submissionDate: asString(record.submissionDate),
        title: asString(record.title),
        position: asString(record.position),
        currentStatus: asString(record.currentStatus),
        disqualified: asString(record.disqualified),
    }
}

const parseDirector = (raw: unknown): BoardDirector | undefined => {
    const record = asRecord(raw)
    if (!record) {
        return undefined
    }
    const name = asString(record.name)
    if (!name) {
        return undefined
    }
    const historyRaw = Array.isArray(record.history) ? record.history : []
    return {
        title: asString(record.title),
        name,
        din: asString(record.din),
        pan: asString(record.pan),
        position: asString(record.position),
        currentStatus: asString(record.currentStatus),
        disqualified: asString(record.disqualified),
        history: historyRaw.map(parseHistoryEntry).filter((entry): entry is BoardDirectorHistoryEntry => !!entry),
    }
}

export const parseBoardOfDirectorsDocument = (raw: unknown): BoardOfDirectorsDocument | undefined => {
    const record = asRecord(raw)
    if (!record) {
        return undefined
    }
    const directorsRaw = Array.isArray(record.directors) ? record.directors : []
    const directors = directorsRaw.map(parseDirector).filter((director): director is BoardDirector => !!director)
    if (directors.length === 0 && !asString(record.symbol)) {
        return undefined
    }
    return {
        symbol: asString(record.symbol),
        exchange: asString(record.exchange),
        asOfQuarterEndDate: asString(record.asOfQuarterEndDate),
        updatedAt: asString(record.updatedAt),
        directorCount: typeof record.directorCount === 'number' ? record.directorCount : directors.length,
        directors,
    }
}

export const parseDirectorByDinDocument = (raw: unknown): DirectorByDinDocument | undefined => {
    const record = asRecord(raw)
    if (!record) {
        return undefined
    }
    const din = asString(record.din)
    if (!din) {
        return undefined
    }
    const rolesRaw = Array.isArray(record.roles) ? record.roles : []
    const roles = rolesRaw
        .map((roleRaw) => {
            const role = asRecord(roleRaw)
            if (!role) {
                return undefined
            }
            return {
                symbol: asString(role.symbol),
                exchange: asString(role.exchange),
                quarterEndDate: asString(role.quarterEndDate),
                submissionDate: asString(role.submissionDate),
                title: asString(role.title),
                name: asString(role.name),
                position: asString(role.position),
                currentStatus: asString(role.currentStatus),
                disqualified: asString(role.disqualified),
            } satisfies DirectorRoleHistoryEntry
        })
        .filter((role): role is DirectorRoleHistoryEntry => !!role)

    return {
        din,
        name: asString(record.name),
        title: asString(record.title),
        pan: asString(record.pan),
        updatedAt: asString(record.updatedAt),
        roleCount: typeof record.roleCount === 'number' ? record.roleCount : roles.length,
        roles,
    }
}

export const formatDirectorDisplayName = (director: Pick<BoardDirector, 'title' | 'name'>): string => {
    const name = director.name.trim()
    if (!name) {
        return '—'
    }
    return name
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}

export const formatBoardPosition = (position: string): string => {
    const trimmed = position.trim()
    return trimmed || '—'
}

/** "31-DEC-2024" → "Dec 24" */
export const formatBoardAsOfLabel = (quarterEndDate: string | undefined): string => {
    const text = (quarterEndDate || '').trim()
    if (!text) {
        return '—'
    }
    const match = text.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/)
    if (!match) {
        return text
    }
    const monthToken = match[2].slice(0, 1).toUpperCase() + match[2].slice(1).toLowerCase()
    const year = match[3].length === 4 ? match[3].slice(2) : match[3]
    return `${monthToken} ${year}`
}

/** "31-DEC-2024" → 2024 */
export const formatBoardYearLabel = (quarterEndDate: string | undefined): string => {
    const text = (quarterEndDate || '').trim()
    const match = text.match(/-(\d{4})$/)
    if (match) {
        return match[1]
    }
    const parsed = Date.parse(text)
    if (!Number.isNaN(parsed)) {
        return String(new Date(parsed).getFullYear())
    }
    return text || '—'
}

const normalizeQuarterEndDate = (quarterEndDate: string | undefined): string =>
    (quarterEndDate || '').trim().toUpperCase()

/** Parse NSE-style quarter labels for ordering; invalid → 0. */
const parseQuarterEndDateMs = (quarterEndDate: string | undefined): number => {
    const text = (quarterEndDate || '').trim()
    if (!text) {
        return 0
    }
    const match = text.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/)
    if (match) {
        const day = Number(match[1])
        const monthToken = match[2].slice(0, 1).toUpperCase() + match[2].slice(1).toLowerCase()
        const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3])
        const parsed = Date.parse(`${day} ${monthToken} ${year} UTC`)
        return Number.isNaN(parsed) ? 0 : parsed
    }
    const parsed = Date.parse(text)
    return Number.isNaN(parsed) ? 0 : parsed
}

/** Company-level latest quarter (document field, else max history date). */
export const resolveBoardAsOfQuarter = (document: BoardOfDirectorsDocument | undefined): string => {
    if (!document) {
        return ''
    }
    const declared = normalizeQuarterEndDate(document.asOfQuarterEndDate)
    if (declared) {
        return declared
    }
    let best = ''
    let bestMs = 0
    for (const director of document.directors) {
        for (const entry of director.history) {
            const quarter = normalizeQuarterEndDate(entry.quarterEndDate)
            const ms = parseQuarterEndDateMs(quarter)
            if (ms > bestMs) {
                bestMs = ms
                best = quarter
            }
        }
    }
    return best
}

/**
 * Directors present in the company's latest board quarter only.
 * Top-level fields are taken from that quarter's history entry so every row
 * matches asOf (e.g. Jun 2026), not an older personal last-seen date.
 */
export const getCurrentBoardDirectors = (document: BoardOfDirectorsDocument | undefined): BoardDirector[] => {
    if (!document) {
        return []
    }
    const asOf = resolveBoardAsOfQuarter(document)
    if (!asOf) {
        return sortBoardDirectors(document.directors)
    }

    const rows: BoardDirector[] = []
    for (const director of document.directors) {
        const entry = director.history.find(
            (history) => normalizeQuarterEndDate(history.quarterEndDate) === asOf,
        )
        if (!entry) {
            continue
        }
        rows.push({
            ...director,
            title: entry.title || director.title,
            position: entry.position || director.position,
            currentStatus: entry.currentStatus || director.currentStatus,
            disqualified: entry.disqualified || director.disqualified,
        })
    }
    return sortBoardDirectors(rows)
}

export const hasBoardOfDirectorsData = (document: BoardOfDirectorsDocument | undefined): boolean =>
    getCurrentBoardDirectors(document).length > 0

/** Prefer Active directors first, then name. */
export const sortBoardDirectors = (directors: BoardDirector[]): BoardDirector[] =>
    [...directors].sort((a, b) => {
        const aActive = a.currentStatus.toLowerCase() === 'active' ? 0 : 1
        const bActive = b.currentStatus.toLowerCase() === 'active' ? 0 : 1
        if (aActive !== bActive) {
            return aActive - bActive
        }
        return formatDirectorDisplayName(a).localeCompare(formatDirectorDisplayName(b))
    })

export const BOARD_OF_DIRECTORS_COLLAPSED_ROW_COUNT = 6

export const isIndependentPosition = (position: string): boolean =>
    /independent/i.test(position)

export const formatIndependenceLabel = (position: string): string =>
    isIndependentPosition(position) ? 'Independent' : 'Non-Independent'
